import { createColumnHelper } from '@tanstack/react-table';

import { TextCell } from '@/cip/widgets/Table/TextCell';
import { titleCase } from 'utils';

import {
    CashAllocationEnriched,
    CashExposureType,
    ConvictionAllocation,
    ExposureStatus,
    ExposureTypes,
    FilterType,
    GrowthAllocationEnriched,
    GrowthAllocationType,
    SecuredAllocationEnriched,
} from '../_openapi/portfolio-optimizer';
import {
    baseCol,
    dateCol,
    moneyCol,
    numberCol,
    percentageCol,
    roundedNumberCol,
    textCol,
} from '../widgets/Table/columnHelpers/colDef';

const growthAllocationColumnHelper = createColumnHelper<GrowthAllocationEnriched>();
export const GrowthAllocationListColumns = [
    growthAllocationColumnHelper.accessor('pId', textCol({ id: 'pId' })),
    growthAllocationColumnHelper.accessor('portfolioGuid', textCol({ id: 'portfolioGuid' })),
    growthAllocationColumnHelper.accessor('portfolioName', textCol({ id: 'portfolioName' })),
    growthAllocationColumnHelper.accessor('householdName', textCol({ id: 'householdName' })),
    growthAllocationColumnHelper.accessor('referencePortfolio', textCol({ id: 'referencePortfolio' })),
    growthAllocationColumnHelper.accessor(
        'allocationType',
        textCol({
            id: 'allocationType',
            meta: {
                filterOptionsFn: () => Object.values(GrowthAllocationType),
                filterType: FilterType.LIST,
            },
        }),
    ),
    growthAllocationColumnHelper.accessor('currentExposure', roundedNumberCol({ id: 'currentExposure' })),
    growthAllocationColumnHelper.accessor('targetExposure', roundedNumberCol({ id: 'targetExposure' })),
    growthAllocationColumnHelper.accessor('activeExposure', roundedNumberCol({ id: 'activeExposure' })),
    growthAllocationColumnHelper.accessor(
        'exposureStatus',
        textCol({
            id: 'exposureStatus',
            meta: {
                filterOptionsFn: () => Object.values(ExposureStatus),
                filterType: FilterType.LIST,
            },
        }),
    ),
    growthAllocationColumnHelper.accessor('currentWeight', percentageCol({ id: 'currentWeight' })),
    growthAllocationColumnHelper.accessor('targetWeight', percentageCol({ id: 'targetWeight' })),
    growthAllocationColumnHelper.accessor('activeWeight', percentageCol({ id: 'activeWeight' })),
    growthAllocationColumnHelper.accessor('targetGrowthSleeve', moneyCol({ id: 'targetGrowthSleeve' })),
    growthAllocationColumnHelper.accessor('actualUnencumberedCash', moneyCol({ id: 'actualUnencumberedCash' })),
    growthAllocationColumnHelper.accessor('currentLeverageRatio', numberCol({ id: 'currentLeverageRatio' })),
    growthAllocationColumnHelper.accessor('reportStage', textCol({ id: 'reportStage' })),
    growthAllocationColumnHelper.accessor('tradeDate', dateCol({ id: 'tradeDate' })),
];

const securedAllocationColumnHelper = createColumnHelper<SecuredAllocationEnriched>();
export const SecuredAllocationListColumns = [
    securedAllocationColumnHelper.accessor('pId', textCol({ id: 'pId' })),
    securedAllocationColumnHelper.accessor('portfolioGuid', textCol({ id: 'portfolioGuid' })),
    securedAllocationColumnHelper.accessor('portfolioName', textCol({ id: 'portfolioName' })),
    securedAllocationColumnHelper.accessor('householdName', textCol({ id: 'householdName' })),
    securedAllocationColumnHelper.accessor('exposureType', {
        ...baseCol({ id: 'exposureType' }),
        cell: ({ getValue, table, column }) => {
            const value = getValue();
            const colFilter = table.getState().columnFilters.find((filter) => column.id === filter.id);
            const highlight = colFilter?.value as string[] | undefined;
            return <TextCell value={value ? titleCase(value) : value} highlight={highlight} />;
        },
        meta: {
            filterOptionsFn: () => Object.values(ExposureTypes),
            filterType: FilterType.LIST,
        },
    }),
    securedAllocationColumnHelper.accessor('currentExposure', roundedNumberCol({ id: 'currentExposure' })),
    securedAllocationColumnHelper.accessor('targetExposure', roundedNumberCol({ id: 'targetExposure' })),
    securedAllocationColumnHelper.accessor('activeExposure', roundedNumberCol({ id: 'activeExposure' })),
    securedAllocationColumnHelper.accessor(
        'exposureStatus',
        textCol({
            id: 'exposureStatus',
            meta: {
                filterOptionsFn: () => Object.values(ExposureStatus),
                filterType: FilterType.LIST,
            },
        }),
    ),
    securedAllocationColumnHelper.accessor('maxBuyConstraint', roundedNumberCol({ id: 'maxBuyConstraint' })),
    securedAllocationColumnHelper.accessor('targetSecuredness', percentageCol({ id: 'targetSecuredness' })),
    securedAllocationColumnHelper.accessor(
        'previousTargetSecuredness',
        percentageCol({ id: 'previousTargetSecuredness' }),
    ),
    securedAllocationColumnHelper.accessor('initialSecuredness', percentageCol({ id: 'initialSecuredness' })),
    securedAllocationColumnHelper.accessor('targetDuration', numberCol({ id: 'targetDuration' })),
    securedAllocationColumnHelper.accessor('initialDuration', numberCol({ id: 'initialDuration' })),
    securedAllocationColumnHelper.accessor('targetFundedness', numberCol({ id: 'targetFundedness' })),
    securedAllocationColumnHelper.accessor('initialFundedness', numberCol({ id: 'initialFundedness' })),
];

const convictionAllocationColumnHelper = createColumnHelper<ConvictionAllocation>();
export const ConvictionAllocationListColumns = [
    convictionAllocationColumnHelper.accessor('allocationType', textCol({ id: 'allocationType' })),
    convictionAllocationColumnHelper.accessor('currentExposure', roundedNumberCol({ id: 'currentExposure' })),
    convictionAllocationColumnHelper.accessor('targetExposure', roundedNumberCol({ id: 'targetExposure' })),
    convictionAllocationColumnHelper.accessor('activeExposure', roundedNumberCol({ id: 'activeExposure' })),
    convictionAllocationColumnHelper.accessor((row) => getExposureStatus(row.activeExposure), {
        ...baseCol({ id: 'exposureStatus' }),
        cell: ({ getValue }) => <TextCell value={getValue()} />,
    }),
    convictionAllocationColumnHelper.accessor('maxBuyConstraint', roundedNumberCol({ id: 'maxBuyConstraint' })),
    convictionAllocationColumnHelper.accessor('maxTargetExposure', roundedNumberCol({ id: 'maxTargetExposure' })),
];

const cashAllocationColumnHelper = createColumnHelper<CashAllocationEnriched>();
export const CashAllocationListColumns = [
    cashAllocationColumnHelper.accessor('pId', textCol({ id: 'pId' })),
    cashAllocationColumnHelper.accessor('portfolioGuid', textCol({ id: 'portfolioGuid' })),
    cashAllocationColumnHelper.accessor('portfolioName', textCol({ id: 'portfolioName' })),
    cashAllocationColumnHelper.accessor('householdName', textCol({ id: 'householdName' })),
    cashAllocationColumnHelper.accessor('exposureType', {
        ...baseCol({ id: 'exposureType' }),
        cell: ({ getValue, table, column }) => {
            const value = getValue();
            const colFilter = table.getState().columnFilters.find((filter) => column.id === filter.id);
            const highlight = colFilter?.value as string[] | undefined;
            return <TextCell value={value ? titleCase(value) : value} highlight={highlight} />;
        },
        meta: {
            filterOptionsFn: () => Object.values(CashExposureType),
            filterType: FilterType.LIST,
        },
    }),
    cashAllocationColumnHelper.accessor('currentExposure', roundedNumberCol({ id: 'currentExposure' })),
    cashAllocationColumnHelper.accessor('targetExposure', roundedNumberCol({ id: 'targetExposure' })),
    cashAllocationColumnHelper.accessor('activeExposure', roundedNumberCol({ id: 'activeExposure' })),
];

const getExposureStatus = (activeExposure?: number): ExposureStatus | undefined => {
    if (activeExposure === undefined) {
        return undefined;
    }
    if (activeExposure > 0) {
        return ExposureStatus.OVERWEIGHT;
    }
    if (activeExposure < 0) {
        return ExposureStatus.UNDERWEIGHT;
    }
    return ExposureStatus.ON_TARGET;
};
